import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetProjectByIdQuery,
  useGetProjectByTokenQuery,
  useGetProjectsQuery,
} from "../../../api/projectsApi";
import SidebarParticipants from "../../../components/Sidebar/components/SidebarParticipants/SidebarParticipants";
import Icon from "../../../components/Icon/Icon";
import StatusTag from "../../../components/StatusTag/StatusTag";
import {
  IconColorE,
  IconSizeE,
  ProjectStatusE,
  StatusE,
  UserRoleE,
  VideoContentStatusE,
} from "../../../models/enums";
import ProjectInfo from "../components/ProjectInfo/ProjectInfo";
import Button, { ButtonModeE } from "../../../components/Button/Button";
import { H3, P, Span } from "../../../components/Text/Text";
import VideoContentRow from "../components/VideoContentRow/VideoContentRow";
import Tabs from "../../../components/Tabs/Tabs";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import Uploader from "../../../components/Uploader/Uploader";
import ShareOptions from "../../../components/ShareOptions/ShareOptions";
import { useNonDesktopViewport } from "../../../utils/hooks/useNonDesktopViewport";
import { useWindowResize } from "../../../utils/hooks/useWindowResize";
import ParticipantsInfo from "../../../components/ParticipantsInfo/ParticipantsInfo";
import ShareSlider from "../../../sliders/ShareSlider/ShareSlider";
import ProjectInfoSlider from "../../../sliders/ProjectInfoSlider/ProjectInfoSlider";
import { UserT, VersionT } from "../../../models/models";
import AccessRights from "../../../components/AccessRights/AccessRights";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  clearHouseNum,
  setShareToken,
  setDeliveryServiceStep,
  setIsDeliveryServicePaymentAvailable,
  setUserMailSharedProject,
} from "../../../store/slices/appSlice";
import { useCampaignWizard } from "../../../popups/CampaignWizard/useCampaignWizard";
import ProjectDataLogs from "../../../components/ProjectDataLogs/ProjectDataLogs";
import DeliverySidebar from "../../../components/DeliverySidebar/DeliverySidebar";
import dayjs from "dayjs";
import TimerIcon from "../../../components/TimerIcon/TimerIcon";
import { statusFilters } from "../../../utils/helpers/statusFilters";
import { clss } from "../../../utils/helpers/styleHelpers";
import { DATE_FORMAT } from "../../../utils/constants";
import NotFound from "../../NotFound/NotFound";
import useDeliveryActions from "../../../utils/hooks/useDeliveryActions";
import Breadcrumbs from "../../../components/Breadcrumbs/Breadcrumbs";
import { useTranslate } from "../../../utils/hooks/useTranslate";
import getCampaignNameWithoutDate from "../../../utils/helpers/getCampaignNameWithoutDate";
import usePopup from "../../../utils/hooks/usePopup";
import WaitForEncoding from "../../../popups/WaitForEncoding";
import { selectCurrentUser } from "../../../store/selectors/authSelectors";
import RedDot from "../../../components/RedDot/RedDot";
import ReDeliveryIndicator from "../../../components/ReDeliveryIndicator/ReDeliveryIndicator";
import InternalNumberButton from "../../../components/InternalNumberButton/InternalNumberButton";
import DollarSign from "../../../components/DollarSign/DollarSign";
import { useCopyText } from "../../../utils/helpers/copyText";
import { useWizardCreator } from "../../../popups/Wizard/useWizardCreator";
import { WizardModeE, WizardTypeE } from "../../../popups/Wizard/models";
import UploadRawMaterials from "../../../components/UploadRawMaterials/UploadRawMaterials";
import hasMXF from "../helpers/hasMXF";
import isRawMaterialsAllowed from "../helpers/isRawMaterialsAllowed";
import {
  isCompletedDeliveryServiceView,
  isDeliveryServiceView,
} from "../helpers/isCompletedDeliveryServiceView";
import { isRawAllowedForBroadcaster } from "../../../utils/helpers/isRawAllowedForBroadcaster";
import { isDeliveryOrderPaid } from "../../../utils/helpers/isDeliveryOrderPaid";

import styles from "./viewVideoProject.module.scss";
import { useGetBroadcastersOverlayPlayerQuery } from "../../../api/clientAPI";

const mobileTabs = ["Version", "Participants"];
const POLLING_INTERVAL = 10000;

type ViewVideoProjectPropTypes = {
  token?: string;
  user?: UserT;
};



const ViewVideoProject: FC<ViewVideoProjectPropTypes> = (props) => {
  const { user: userProp, token } = props;
  const isNonDesktop = useNonDesktopViewport();
  const [windowWidth] = useWindowResize();
  const isViewportMobile = windowWidth > 0 && windowWidth <= 769;
  const userLocal = useAppSelector(selectCurrentUser);
  const [user, setUser] = useState(userLocal || userProp);
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [pollingInterval, setPollingInterval] = useState<number | false>(
    POLLING_INTERVAL
  );
  const [data, setData] = useState(null);
  const {
    data: authData,
    refetch: refetchAuth,
    isLoading,
    isError,
  } = useGetProjectByIdQuery(
    { id: projectId },
    {
      //@ts-ignore
      pollingInterval: !token ? (pollingInterval as number) : false,
      skip: !!token,
    }
  );
  const { data: unAuthData, refetch: refetchUnAuth, isLoading: isUnAuthLoading } =
    useGetProjectByTokenQuery(
      { token },
      {
        //@ts-ignore
        pollingInterval: token ? (pollingInterval as number) : false,
        skip: !token,
      }
    );
  const { data: relatedDeliveriesData } = useGetProjectsQuery(
    {
      take: 100,
      page: 1,
      limit: 100,
      filterType: "campaignNumber",
      filter: data?.Campaign?.Id,
    },
    {
      skip: !data?.Campaign?.Id || !!token,
    }
  );

  const { data: broadcastersOverlayPlayerData } = useGetBroadcastersOverlayPlayerQuery(
    { projectId },
    // Skip on the shared/guest route (token flow): this endpoint is auth-only and
    // would 401, and projectId from the URL is undefined there anyway.
    { skip: !!token || !projectId }
  );

  const { createPopup } = usePopup();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedMobileTab, setSelectedMobileTab] = useState(0);
  const [showMore, setShowMore] = useState(!isNonDesktop);

  useLayoutEffect(() => {
    setShowMore(!isNonDesktop);
  }, [isNonDesktop]);
  const [showShareContextMenu, setShowShareContextMenu] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [showFloatingHeader, setShowFloatingHeader] = useState(false);
  const [showInfoBottomSlider, setShowInfoBottomSlider] = useState(false);
  const [showShareBottomSlider, setShowShareBottomSlider] = useState(false);
  const [termsRead, setTermsRead] = useState(false);
  const shareRef = useRef(null);
  const uploaderRef = useRef(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslate();
  const copyText = useCopyText();
  const createWizard = useWizardCreator();
  const {
    defaultActions, // actions for icons
    sendForCustomerApproval,
    sendToBroadcasters,
    saveAndContinue,
    sendDelivery,
    shareDelivery,
    handleChangeInternalNumber,
  } = useDeliveryActions(data, token ? refetchUnAuth : refetchAuth);

  // spec-41 — the two predicates behind every delivery-service difference on this page.
  // `isCompletedDeliveryService` is the ONE gate for the new paid layout (status badge, the
  // destination/media-company panel, the download log). While the order is unpaid it is false and
  // the page stays byte-identical to what the live create → upload → pay flow renders today (D2/R4).
  const isDeliveryServiceUser = isDeliveryServiceView(user?.role);
  const isCompletedDeliveryService = isCompletedDeliveryServiceView(
    user?.role,
    data?.DeliveryServiceDetailsViewModel?.OrderStatus
  );

  // Entry (b): re-broadcast / reuse a past delivery now renders via the shared delivery actions
  // (`defaultActions` → getAvailableActions, "Reuse this delivery") so there is a single action icon
  // with a tooltip matching the actions dropdown.

  const handleShowUploader = useCallback(() => {
    setShowUploader(true);
  }, []);

  useEffect(() => {
    setData(authData || unAuthData);
    dispatch(
      // Spec 29 / handoff C11: keep the link's signed token (echoed by SharedVideoAdProject), never a user id.
      setShareToken(
        data?.ShareToken || unAuthData?.ShareToken
      )
    );
    dispatch(setUserMailSharedProject(unAuthData?.UserMailSharedProject));

    return () => {
      dispatch(setShareToken(""));
    };
  }, [authData, unAuthData]);

  useEffect(() => {
    dispatch(setDeliveryServiceStep(2));
    document.addEventListener("show-uploader", handleShowUploader);

    return () => {
      dispatch(clearHouseNum());
      document.removeEventListener("show-uploader", handleShowUploader);
    };
  }, []);

  useEffect(() => {
    if (data) {
      dispatch(
        setIsDeliveryServicePaymentAvailable(
          data?.StatusName === StatusE.WaitingForApprovalOfAdvertisingAgency
        )
      );
    }
  }, [data?.StatusName]);

  useEffect(() => {
    if (
      data &&
      user &&
      ((user.role === UserRoleE.PostProduction &&
        (data.StatusName === StatusE.WaitingForApprovalOfPostProduction ||
          data.StatusName === StatusE.Opened)) ||
        user.role === UserRoleE.DeliveryService) &&
      data?.Detailed?.UploadAllowed
    ) {
      setShowUploader(true);
    }
  }, [data?.StatusName, data?.Detailed?.UploadAllowed, user?.role]);

  useEffect(() => {
    const approveButton: HTMLButtonElement = document.getElementById(
      "send-delivery-button"
    ) as HTMLButtonElement;

    if (approveButton) {
      approveButton.disabled = !termsRead;
      approveButton.style.pointerEvents = !termsRead ? "none" : "all";
    }
  }, [termsRead]);

  useEffect(() => {
    setPollingInterval(isError ? false : POLLING_INTERVAL);
  }, [isError]);

  const onScroll = (e: any) => {
    setShowFloatingHeader(e.target.scrollTop >= 154);
  };

  const toggleShowMore = () => {
    setShowMore((prev) => !prev);
  };

  const toggleShowShare = () => {
    if (isNonDesktop) {
      setShowShareBottomSlider((prev) => !prev);
    } else {
      setShowShareContextMenu((prev) => !prev);
    }
  };

  const toggleShowUploader = () => {
    setShowUploader((prev) => !prev);
  };

  // const toggleLogsBottomSlider = () => {
  //     setShowLogsBottomSlider(prev => !prev);
  // };

  const toggleInfoBottomSlider = () => {
    setShowInfoBottomSlider((prev) => !prev);
  };

  const navigateToDeliveryInfo = (e) => {
    e.preventDefault();
    navigate(`/campaigns/${data.Campaign.Id}`);
  };

  const handleGoToUploader = () => {
    setShowUploader(true);
    uploaderRef && uploaderRef.current.scrollIntoView();
  };

  const handleCloseUploader = () => {
    setShowUploader(false);
  };

  const navigateToRelatedDelivery = (e, projectId: number) => {
    e.preventDefault();
    navigate(`/deliveries/${projectId}`);
  };

  const handleFullEdit = () => {
    const wizardPopup = createWizard({
      type: WizardTypeE.Delivery,
      mode: WizardModeE.Edit,
      deliveryId: data?.Id,
      campaignDeliveryId: data?.Id,
    });
    createPopup(wizardPopup);
    // createCampaignWizard(data?.Id, CampaignWizardMode.EDIT, data.StatusName);
  };

  const handleApproveAndContinue = (setTermsRead) => {
    // if versions uploading not finished
    if (
      data.Versions.some(
        ({ StatusName }) =>
          StatusName === VideoContentStatusE.ENCODING ||
          StatusName === VideoContentStatusE.UPLOADING ||
          StatusName === VideoContentStatusE.UPLOADED
      )
    ) {
      createPopup(WaitForEncoding);
    } else {
      saveAndContinue(setTermsRead);
    }
  };

  const handleUploadMobile = () => {
    setShowUploader(true);
    setTimeout(() => {
      const element = document.getElementById("uploaderWrapper");
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
        });
      }
    }, 0);
  };

  const navigateToPayment = () => {
    navigate(`/projects/order-payment/${projectId}`);
  };

  const ProjectControlButtons = useCallback(() => {
    return (
      <div className={styles.icons}>
        <InternalNumberButton
          internalNumber={data?.Detailed?.InternalNumber}
          InternalNumberChangeAllowed={
            data?.Detailed?.InternalNumberChangeAllowed
          }
          handleChangeInternalNumber={handleChangeInternalNumber}
        />
        {defaultActions
          .filter(
            ({ action, icon }) =>
              !!action &&
              icon !== "download" &&
              icon !== "sendBroadcast" &&
              icon !== "upload" &&
              icon !== "send"
          )
          .map((action) => (
            <AccessRights
              accessFor={action.accessFor}
              condition={action.condition}
              key={action.name}
            >
              <Icon
                name={action.icon}
                alt={action.name}
                title={action.name}
                onClick={action.action}
              />
            </AccessRights>
          ))}
        {data && !token && (
          <div ref={shareRef}>
            <Icon
              name="share"
              alt="share"
              title="share"
              onClick={toggleShowShare}
            />
          </div>
        )}
      </div>
    );
  }, [data?.Status]);

  const FloatingHeader = useCallback(
    () => (
      <div className={styles.floatingHeaderWrapper}>
        <div className={styles.floatingProjectName}>
          <Breadcrumbs
            campaignNumber={data?.Campaign?.Id}
            campaignName={data?.Campaign?.Name}
            deliveryNumber={+data?.Number}
            deliveryName={
              getCampaignNameWithoutDate(
                data?.ProjectComment || data?.Campaign?.Name
              ) || t`No description`
            }
          />
        </div>
        <div>
          <div className={styles.statusWrapper}>
            {/* spec-41 D5 — no clock for a delivery-service user on a finished, paid delivery:
                there is nothing left for them to act on. Note this icon is NOT role-gated here
                (unlike the main header), and `showWaitingActionIconFor` keys off clientType, not
                role — so a PRIVATE delivery-service user (clientType 0, AccessLevel 4) does get a
                clock at WaitingForApprovalOfAdvertisingAgency. Suppressing it only on the paid
                view leaves the unpaid flow exactly as it is (D2). */}
            {!isCompletedDeliveryService && (
              <TimerIcon
                statusName={data.StatusName}
                clientType={user.clientType}
                role={user.role}
              />
            )}
            {!isDeliveryServiceUser || isCompletedDeliveryService ? (
              <StatusTag
                status={
                  Object.entries(ProjectStatusE).find(
                    (status) => +status[1] === +data?.Status
                  )[0]
                }
                isRemovable={false}
              />
            ) : (
              <span></span>
            )}
          </div>
          <ProjectControlButtons />
        </div>
      </div>
    ),
    // 🔴 spec-41 — `isCompletedDeliveryService` reads OrderStatus, which is NOT covered by
    // data?.Status: an order can be paid while the project sits at
    // WaitingForApprovalOfAdvertisingAgency, so without this dep the floating header would keep
    // rendering the pre-payment version of the badge after the charge lands.
    [data?.Status, data?.Name, isDeliveryServiceUser, isCompletedDeliveryService]
  );

  return isError ? (
    <NotFound />
  ) : data ? (
    <>
      {showShareContextMenu && (
        <ShareOptions
          onClose={() => setShowShareContextMenu(false)}
          type="delivery"
          viewProjectPage
          Id={!!token ? data.Id : +projectId}
          videoUrl={window.location.href}
        />
      )}
      {/* spec-41 R1 — three-way, replacing the old "delivery service or not":
            not DS            -> the participants panel, unchanged
            DS + paid         -> the participants panel, DS variant (media company + destinations)
            DS + not paid     -> the 1/2/3 step sidebar, unchanged (D2/R4) */}
      {!isDeliveryServiceUser || isCompletedDeliveryService ? (
        <SidebarParticipants
          participants={data?.Detailed?.Recepients}
          broadcasters={data?.Detailed?.Broadcasters}
          productionCompanyData={{
            name: data?.Detailed?.ProductionCompanyName,
            id: data?.ProductionCompanyId,
          }}
          mediaCompanyData={{
            name: data?.Detailed?.MediaCompanyName,
            id: data?.MediaCompanyId,
          }}
          variant={isCompletedDeliveryService ? "deliveryService" : "default"}
          // Keeps the profile block the DS user sees on every other page of their flow
          // (DeliveryServiceIndex, OrderPayment) rather than dropping it only here.
          showProfile={isCompletedDeliveryService}
        />
      ) : (
        !isNonDesktop && <DeliverySidebar />
      )}
      <main onScroll={onScroll} className={styles.mainWrapper}>
        <div className={styles.headerWrapper}>
          <Breadcrumbs
            campaignNumber={data?.Campaign?.Id}
            campaignName={data?.Campaign?.Name}
            deliveryNumber={data?.Number}
            deliveryName={
              getCampaignNameWithoutDate(
                data?.ProjectComment || data?.Campaign?.Name
              ) || t`No description`
            }
          />
          {!isNonDesktop && <ProjectControlButtons />}
        </div>
        {showFloatingHeader && <FloatingHeader />}
        {!isLoading ? (
          <div className={styles.projectWrapper}>
            <div className={styles.projectTitle}>
              <div>
                <Icon
                  name="copy"
                  onClick={() => copyText(data?.Number)}
                  color={IconColorE.BLUE}
                  size={isNonDesktop ? IconSizeE.S : IconSizeE.M}
                  className={styles.copyIcon}
                />
                <span>#{data?.Number}</span>
                <span>
                  {data?.ProjectComment ||
                    data?.Campaign?.Name ||
                    t`No description`}
                </span>
                <RedDot isReactProject={data?.IsReactProject} />
                {!isNonDesktop && (
                  <DollarSign
                    condition={
                      data?.DeliveryServiceDetailsViewModel?.OrderStatusName ===
                      "Billed"
                    }
                  />
                )}
                <ReDeliveryIndicator
                  isReDelivery={data?.IsReDelivery}
                  sourceNumber={data?.SourceDeliveryNumber}
                  href={
                    data?.SourceProjectId
                      ? `/deliveries/${data.SourceProjectId}`
                      : null
                  }
                  type="delivery"
                  showNumber
                  style={{ marginInlineStart: 10 }}
                />
              </div>
              <div className={styles.flexBox}>
                {/* spec-41 R2/D5 — the badge and the clock are split. The badge now also shows for
                    a delivery-service user once the delivery is paid for; the clock keeps its
                    original `not a DS user` gate, because `showWaitingActionIconFor` has no
                    DeliveryService rules and would either never fire or fire wrongly. */}
                {(!isDeliveryServiceUser || isCompletedDeliveryService) && (
                  <div className={styles.statusWrapper}>
                    {!isViewportMobile && !isDeliveryServiceUser && (
                      <TimerIcon
                        statusName={data?.StatusName}
                        clientType={user?.clientType}
                        role={user?.role}
                      />
                    )}
                    <StatusTag
                      status={
                        Object.entries(ProjectStatusE).find(
                          (status) => +status[1] === +data?.Status
                        )?.[0]
                      }
                      isRemovable={false}
                    />
                  </div>
                )}
                {isNonDesktop && <ProjectControlButtons />}
              </div>
              {user?.role === UserRoleE.DeliveryService &&
                // data?.StatusName === StatusE.WaitingForApprovalOfPostProduction ||
                data?.StatusName === StatusE.Opened && (
                  <Button
                    icon="edit"
                    onClick={handleFullEdit}
                    className={styles.editCampaignButton}
                    mode={ButtonModeE.LINK}
                  >
                    Edit the delivery
                  </Button>
                )}
            </div>
            <div>
              <ProjectInfo item={data} isProjectView showMore={showMore} />
              {isNonDesktop && (
                <div
                  className={styles.showMoreMobile}
                  onClick={toggleInfoBottomSlider}
                >
                  <div className={styles.showMoreIcon}>
                    <Icon name="info" />
                  </div>
                  <Span>See all info</Span>
                </div>
              )}
              {/* <BottomSlider visible={showLogsBottomSlider} onClose={toggleLogsBottomSlider}> // 27.01.2023 Eran asked to display logs in common flow
                                    <div className={styles.logsSliderWrapper}>
                                        {(projectId || data?.Id) && (
                                            <ProjectDataLogs
                                                projectId={projectId || data?.Id}
                                                pollingInterval={pollingInterval}
                                                selectedTab={selectedTab}
                                                setSelectedTab={setSelectedTab}
                                                shareToken={data?.ShareToken}
                                            />
                                        )}
                                    </div>
                                </BottomSlider> */}
              <ProjectInfoSlider
                showBottomSlider={showInfoBottomSlider}
                toggleShow={toggleInfoBottomSlider}
                data={data}
              />
              <div className={styles.controlSection}>
                <div className={styles.controlButtons}>
                  {/*Upload files mobile button*/}
                  <AccessRights
                    accessFor={[
                      UserRoleE.PostProduction,
                      UserRoleE.DeliveryService,
                    ]}
                    condition={
                      data?.StatusName === StatusE.Opened && isNonDesktop
                    }
                  >
                    <Button onClick={handleUploadMobile} useGlobalLoading>
                      Upload files
                    </Button>
                  </AccessRights>
                  <AccessRights
                    accessFor={[UserRoleE.DeliveryService]}
                    condition={
                      data?.StatusName ===
                      StatusE.WaitingForApprovalOfPostProduction
                      // && data?.Versions.every(version => version.StatusName === VideoContentStatusE.ENCODED)
                    }
                  >
                    <Button
                      onClick={() => handleApproveAndContinue(setTermsRead)}
                      useGlobalLoading
                    >
                      Send project
                    </Button>
                  </AccessRights>
                  <AccessRights
                    accessFor={[UserRoleE.PostProduction]}
                    condition={
                      data?.StatusName ===
                      StatusE.WaitingForApprovalOfPostProduction
                    }
                  >
                    <Button onClick={sendDelivery} useGlobalLoading>
                      Approve and send
                    </Button>
                  </AccessRights>
                  <AccessRights
                    accessFor={[UserRoleE.DeliveryService]}
                    condition={
                      data?.StatusName ===
                      StatusE.WaitingForApprovalOfAdvertisingAgency &&
                      // spec-37 — was `!OrderStatus`, which is a "has no order" test only by
                      // accident: DeliveryOrderStatusE.Created = 0 is falsy, so it passed for a
                      // brand-new order row and failed for the SAME order once a re-submit of the
                      // payment form moved it to Pending(1). That hid the only remaining button at
                      // status 4 — a second dead end reached on the second pass of spec 37's own
                      // recovery loop (delete version → re-upload → Send project → Continue).
                      // The real question is whether the customer still owes us money, and
                      // isDeliveryOrderPaid is the one definition of that (spec 26).
                      !isDeliveryOrderPaid(
                        data?.DeliveryServiceDetailsViewModel?.OrderStatus
                      )
                    }
                  >
                    <Button onClick={navigateToPayment}>Continue</Button>
                  </AccessRights>
                  <AccessRights
                    accessFor={[UserRoleE.AdvertisingAgency]}
                    condition={
                      data?.CreatedByDeliveryServicePrivate &&
                      data?.StatusName ===
                      StatusE.WaitingForApprovalOfAdvertisingAgency &&
                      user?.id === data?.AdvertisingAgencyId
                    }
                  >
                    <Button onClick={() => sendToBroadcasters(data.Id)}>
                      Send to Broadcasters
                    </Button>
                  </AccessRights>
                  {!!token &&
                    !data?.CreatedByDeliveryServicePrivate &&
                    data?.StatusName ===
                    StatusE.WaitingForApprovalOfAdvertisingAgency &&
                    data.IsForCustomerApproval && (
                      <Button onClick={() => sendToBroadcasters(data.Id)}>
                        Send to Broadcasters
                      </Button>
                    )}
                  <AccessRights
                    accessFor={[UserRoleE.AdvertisingAgency]}
                    condition={
                      !data?.CreatedByDeliveryServicePrivate &&
                      data?.StatusName ===
                      StatusE.WaitingForApprovalOfAdvertisingAgency &&
                      !token
                    }
                  >
                    <Button onClick={() => sendToBroadcasters(data.Id)}>
                      Send to Broadcasters
                    </Button>
                    <Button
                      mode={ButtonModeE.OUTLINED}
                      onClick={sendForCustomerApproval}
                    >
                      Send for customer approval
                    </Button>
                  </AccessRights>
                </div>
                {!isNonDesktop && (
                  <Button
                    mode={ButtonModeE.LINK}
                    iconRight
                    icon="moreArrow"
                    onClick={toggleShowMore}
                    className={showMore ? styles.showMoreReversed : ""}
                  >
                    {showMore ? "Less" : "More"} details
                  </Button>
                )}
              </div>

              {/* ------------ Video content ------------ */}

              {isNonDesktop ? (
                <div className={styles.mobileTabsWrapper}>
                  <Tabs
                    setSelectedTab={setSelectedMobileTab}
                    selectedTab={selectedMobileTab}
                    tabs={mobileTabs}
                  />
                  {/* <div className={styles.showLogsIconWrapper}>
                                            <Icon name='reload' onClick={toggleLogsBottomSlider} />
                                        </div> */}
                </div>
              ) : (
                <h3 className={styles.videoContentHeader}>
                  <span>
                    <Span>Versions</Span>
                    {` (${data?.Versions?.length})`}
                  </span>
                  <AccessRights
                    condition={
                      !showUploader &&
                      !isNonDesktop &&
                      data?.Detailed?.UploadAllowed &&
                      data?.Versions?.length > 4
                    }
                  >
                    <Button onClick={handleGoToUploader}>Upload files</Button>
                  </AccessRights>
                </h3>
              )}
              <div className={styles.videoContentWrapper}>
                {selectedMobileTab === 0 ? (
                  <>
                    {data?.Versions?.map((item: VersionT, index) => (
                      /*@ts-ignore*/
                      <VideoContentRow
                        item={item}
                        fileTypes={data.EnumDescriptionItemList}
                        key={item.Id}
                        broadcastingDate={data.BroadcastingDate}
                        refetchProject={token ? refetchUnAuth : refetchAuth}
                        projectId={!!token ? data.Id : +projectId}
                        projectStatus={data.StatusName as StatusE}
                        index={index}
                        editable
                        broadcastersOverlayPlayerData={broadcastersOverlayPlayerData}
                        isFetchingData={isLoading || isUnAuthLoading}
                        // spec-37 — lets the row offer "Remove version" to a delivery-service user at
                        // WaitingForApprovalOfAdvertisingAgency while the order is still unpaid.
                        orderStatus={
                          data?.DeliveryServiceDetailsViewModel?.OrderStatus
                        }
                      />
                    ))}
                    {hasMXF(data?.Versions) &&
                      isRawMaterialsAllowed(data?.BroadcasterIds) &&
                      data?.JumboFormID_EN &&
                      data?.JumboFormID_HE &&
                      (user?.role === UserRoleE.Broadcaster
                        ? isRawAllowedForBroadcaster(data?.StatusName)
                        : true) && (
                        <UploadRawMaterials
                          rawMaterials={data?.AdaptationsRawMaterialsList}
                          campaignNumber={data?.Detailed?.Campaign?.Id}
                          broadcastingDate={data.BroadcastingDate}
                          campaignName={data?.Detailed?.Campaign?.Name}
                          deliveryName={data?.Name}
                          projectId={!!token ? data.Id : +projectId}
                          postProductionCompanyName={
                            data?.Detailed?.PostProductionCompanyName
                          }
                          JumboForm_EN={data?.JumboFormID_EN}
                          JumboForm_HE={data?.JumboFormID_HE}
                          deliveryNumber={data?.Number}
                        />
                      )}
                  </>
                ) : (
                  // spec-41 §4.1 — on mobile the participants live in this tab, not in a sidebar
                  // (DeliverySidebar is desktop-only and SidebarParticipants returns null here), so
                  // this is the only place D4 can be honoured on a phone. Paid deliveries only: on
                  // an unpaid one the tab stays exactly as it is today (D2/R4).
                  <ParticipantsInfo
                    participants={data?.Detailed?.Recepients}
                    broadcasters={data?.Detailed?.Broadcasters}
                    productionCompanyData={{
                      name: data?.Detailed?.ProductionCompanyName,
                      id: data?.ProductionCompanyId,
                    }}
                    mediaCompanyData={{
                      name: data?.Detailed?.MediaCompanyName,
                      id: data?.MediaCompanyId,
                    }}
                    variant={
                      isCompletedDeliveryService ? "deliveryService" : "default"
                    }
                  />
                )}

                {!showUploader &&
                  !isNonDesktop &&
                  data?.Detailed?.UploadAllowed && (
                    <div className={styles.uploadButtonWrapper}>
                      <Button onClick={toggleShowUploader}>Upload files</Button>
                    </div>
                  )}

                {/*-------------------------------------UPLOADER------------------------------------*/}

                <div ref={uploaderRef}>
                  {showUploader && (
                    <div
                      className={styles.uploaderWrapper}
                      id="uploaderWrapper"
                    >
                      <Uploader
                        versions={data.Versions}
                        projectId={data.Id}
                        handleClose={handleCloseUploader}
                        project={data}
                      />
                    </div>
                  )}
                </div>
              </div>
              {data?.Detailed?.Comments?.length && (
                <div className={styles.commentsWrapper}>
                  <H3 className={styles.videoContentHeader}>Comments</H3>
                  <ul className={styles.comments}>
                    {data.Detailed.Comments.map((comment, index) => (
                      <li key={index}>
                        <span>{comment.ClientName}</span>
                        <span>{comment.Body}</span>
                        <span>
                          {dayjs(comment.DateTime).format(
                            `${DATE_FORMAT} HH:mm`
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* spec-41 D1/R3 — was `user?.clientType !== 5`, a magic number that gated this
                  panel on a DIFFERENT population from the role-based gates above it: a private
                  delivery-service user (clientType 0, AccessLevel 4) already saw the full
                  Download + Activity Log here, while a generic JumboDelivery user saw nothing.
                  Now both delivery-service modes get the same page — the Download Log, and only
                  once the delivery is paid for. Per D6 it renders even with zero downloads; the
                  component's own empty state covers that, so there is no "are there downloads"
                  check here. */}
              {(!isDeliveryServiceUser || isCompletedDeliveryService) &&
                (projectId || data?.Id) && (
                  <ProjectDataLogs
                    projectId={projectId || data?.Id}
                    pollingInterval={pollingInterval}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    shareToken={data?.ShareToken}
                    showActivityLog={!isDeliveryServiceUser}
                  />
                )}
              <H3
                className={clss([
                  styles.videoContentHeader,
                  styles.relatedDeliveriesHeader,
                ])}
              >
                Related deliveries
              </H3>
              <ul className={styles.relatedDeliveriesList}>
                {!!relatedDeliveriesData?.Projects?.filter(
                  (project) =>
                    project.Id !== data.Id &&
                    project.StatusName !== ProjectStatusE.Metadata &&
                    project.StatusName !== StatusE.Metadata
                )?.length ? (
                  relatedDeliveriesData?.Projects?.filter(
                    (project) =>
                      project.Id !== data.Id &&
                      project.StatusName !== ProjectStatusE.Metadata &&
                      project.StatusName !== StatusE.Metadata
                  )?.map((project) => (
                    <li>
                      <a
                        href={`/deliveries/${project.Id}`}
                        onClick={(e) =>
                          navigateToRelatedDelivery(e, project.Id)
                        }
                      >
                        #{project.Number} -{" "}
                        {project.ProjectComment || <Span>No description</Span>}
                      </a>
                      <span>
                        {" "}
                        —{" "}
                        <Span>
                          {statusFilters.find(
                            (status) => status.value === project.StatusName
                          )?.label || ""}
                        </Span>
                      </span>
                    </li>
                  ))
                ) : (
                  <P>No related deliveries</P>
                )}
              </ul>
            </div>
            <ShareSlider
              showBottomSlider={showShareBottomSlider}
              toggleShow={toggleShowShare}
              handleShare={shareDelivery}
              name="Campaign"
            />
          </div>
        ) : (
          <div className={styles.loadingWrapper}>
            <Icon
              name="loading"
              size={IconSizeE.XL}
              color={IconColorE.LIGHT_GRAY}
            />
          </div>
        )}
      </main>
    </>
  ) : null;
};

export default ViewVideoProject;
